import { TranslateService } from '@ngx-translate/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServiceTranslation } from '../../ui/models/service-translation.model';

export interface TranslationContext {
  translatedLayout: FormlyFieldConfig[];
  layout: FormlyFieldConfig[];
  serviceTranslations?: IServiceTranslation;
  translateService: TranslateService;
}

export function getTranslatedValue(
  key: any,
  locale: string,
  translateService: TranslateService,
  serviceTranslations?: IServiceTranslation
): string | undefined {
  const globalTranslation = translateService.instant(key);
  return globalTranslation && globalTranslation !== ''
    ? globalTranslation
    : serviceTranslations?.[locale]?.[key];
}

export function translateFormFields(
  translatedBlock: any,
  originalBlock: any,
  locale: string,
  translateService: TranslateService,
  serviceTranslations?: IServiceTranslation
) {
  const fieldProps = [
    'label',
    'hint',
    'placeholder',
    'message',
    'title',
    'extraMessage',
    'internalDescription',
    'tooltip',
    'optionalToggleQuestion',
  ];

  translatedBlock.fieldGroup.forEach((field: any, fieldIndex: number) => {
    const originalField = originalBlock.fieldGroup[fieldIndex];

    fieldProps.forEach(prop => {
      if (originalField.props[prop]) {
        field.props[prop] =
          getTranslatedValue(
            originalField.props[prop],
            locale,
            translateService,
            serviceTranslations
          ) ?? originalField.props[prop];
      }
    });

    if (field.props.options?.length) {
      const fieldBindLabel = field.props.bindLabel ?? 'label';
      field.props.options.forEach((option: any) => {
        const originalLabel = option[fieldBindLabel];
        if (originalLabel) {
          option[fieldBindLabel] =
            getTranslatedValue(
              originalLabel,
              locale,
              translateService,
              serviceTranslations
            ) ?? originalLabel;
        }
      });
    }

    if (Array.isArray(field.props.configs)) {
      field.props.configs.forEach((config: any) => {
        if (config.label) {
          config.label =
            getTranslatedValue(
              config.label,
              locale,
              translateService,
              serviceTranslations
            ) ?? config.label;
        }
        if (config.placeholder) {
          config.placeholder =
            getTranslatedValue(
              config.placeholder,
              locale,
              translateService,
              serviceTranslations
            ) ?? config.placeholder;
        }
      });
    }

    if (
      field.type === 'customsectionmessagewithlink' &&
      Array.isArray(field.props?.links)
    ) {
      field.props.links.forEach((linkItem: any) => {
        if (linkItem.name) {
          linkItem.name =
            getTranslatedValue(
              linkItem.name,
              locale,
              translateService,
              serviceTranslations
            ) ?? linkItem.name;
        }
      });
    }

    if (field.validation?.messages) {
      Object.keys(field.validation.messages).forEach((key: string) => {
        const originalMessage = originalField.validation.messages[key];
        if (originalMessage) {
          field.validation.messages[key] =
            getTranslatedValue(
              originalMessage,
              locale,
              translateService,
              serviceTranslations
            ) ?? originalMessage;
        }
      });

      // Reset form control errors
      if (field['formControl']?.['errors']) {
        const errors = {
          ...field['formControl']?.['errors'],
        };

        const touched = field['formControl']['touched'];

        field['formControl']['errors'] = null;
        field['formControl']['errors'] = {
          ...errors,
        };
        field['formControl']['touched'] = false;

        setTimeout(() => {
          field['formControl']['touched'] = touched;
        }, 1);
      }
    }

    if (field.props.addText) {
      field.props.addText =
        getTranslatedValue(
          originalField.props.addText,
          locale,
          translateService,
          serviceTranslations
        ) ?? originalField.props.addText;
    }

    if (field.fieldArray) {
      const repeaterFields = field.fieldArray.fieldGroup;
      const originalRepeaterFieldGroups = originalField.fieldArray.fieldGroup;

      repeaterFields.forEach((groupField: any, groupIndex: number) => {
        const originalGroupField = originalRepeaterFieldGroups[groupIndex];

        if (groupField.type === 'block') {
          // If it's a block, use translateBlocks for the block and its fields
          if (groupField.fieldGroup) {
            translateBlocks(
              { fieldGroup: [groupField] }, // Wrap in structure expected by translateBlocks
              { fieldGroup: [originalGroupField] },
              locale,
              translateService,
              serviceTranslations
            );
          }
        } else {
          // For non-block fields, use translateFormFields
          translateFormFields(
            { fieldGroup: [groupField] },
            { fieldGroup: [originalGroupField] },
            locale,
            translateService,
            serviceTranslations
          );
        }
      });
    }
  });
}

export function translateBlocks(
  translatedSection: any,
  originalSection: any,
  locale: string,
  translateService: TranslateService,
  serviceTranslations?: IServiceTranslation
) {
  translatedSection.fieldGroup?.forEach((block: any, blockIndex: number) => {
    const originalBlock = originalSection.fieldGroup[blockIndex];

    if (originalBlock?.props?.label) {
      block.props.label =
        getTranslatedValue(
          originalBlock.props.label,
          locale,
          translateService,
          serviceTranslations
        ) ?? originalBlock.props.label;
    }
    if (block.props.hint) {
      block.props.hint =
        getTranslatedValue(
          originalBlock.props.hint,
          locale,
          translateService,
          serviceTranslations
        ) ?? originalBlock.props.hint;
    }

    translateFormFields(
      block,
      originalBlock,
      locale,
      translateService,
      serviceTranslations
    );
  });
}

export function performFormTranslations(
  context: TranslationContext,
  locale: string
): FormlyFieldConfig[] {
  const { translatedLayout, layout, serviceTranslations, translateService } =
    context;

  if (translatedLayout) {
    for (
      let elementIndex = 0;
      elementIndex < translatedLayout.length;
      elementIndex++
    ) {
      if (translatedLayout[elementIndex]?.fieldGroup ?? false) {
        const fieldGroupLength =
          translatedLayout[elementIndex]?.fieldGroup?.length ?? 0;
        for (
          let sectionIndex = 0;
          sectionIndex < fieldGroupLength;
          sectionIndex++
        ) {
          // Section labels
          const section: any =
            translatedLayout[elementIndex].fieldGroup?.[sectionIndex];

          const originalSection: any = (layout as any)[elementIndex].fieldGroup[
            sectionIndex
          ];

          // Update section label using original section
          if (originalSection.props.label) {
            section.props.label =
              getTranslatedValue(
                originalSection.props.label,
                locale,
                translateService,
                serviceTranslations
              ) ?? originalSection.props.label;
          }

          translateBlocks(
            section,
            originalSection,
            locale,
            translateService,
            serviceTranslations
          );
        }
      }
    }
  }

  return translatedLayout;
}

export function resetFormTranslations(
  layout: FormlyFieldConfig[]
): FormlyFieldConfig[] {
  return JSON.parse(JSON.stringify(layout));
}
